import React from "react";
import { useState, useMemo, useCallback } from "react";
import { cn } from "@/lib/utils";
import { ProductTagsPopover } from "@/components/bulk-ads/ProductTagsPopover";
import { Trash2, Copy, Plus, Loader2, Lock, Pencil, ImagePlus, ChevronDown, Eye, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AdPreviewPanel } from "./AdPreviewPanel";
import { useSelectedRowPreview } from "@/hooks/useSelectedRowPreview";
import { Button } from "@/components/ui/button";
import { CATALOG_HEADLINE_MACROS } from "@/types/bulkAd";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useQueryClient } from "@tanstack/react-query";
import { TextCell, SelectCell, CreativeCell, StatusCell, ScheduleCell, ProductSetCell, InstantFormCell } from "./cells";
import { CarouselCardEditor } from "@/components/ads/CarouselCardEditor";
import { CollectionPopover } from "./CollectionPopover";
import { CatalogPopover } from "./CatalogPopover";
import type { AdRow, Campaign, AdSet, DropdownOptions } from "./types";
import type { AdFormat, AdStage, OptionalColumn, FlexibleData } from "@/types/bulkAd";
import { getFieldRules, type FieldRules } from "@/hooks/useAdSetFieldRules";

interface AdSheetTableProps {
  rows: AdRow[];
  updateRow: (rowId: string, field: keyof AdRow | string, value: unknown) => void;
  updateRowBatch: (rowId: string, updates: Partial<AdRow>) => void;
  deleteRow: (rowId: string) => void;
  onDuplicateRow: (rowId: string) => void;
  dropdownOptions: DropdownOptions;
  getAdSetsForCampaign: (campaignId: string) => AdSet[];
  getAdSetWithDetails: (campaignId: string, adSetId: string) => AdSet | null;
  onSelectCreative: (rowId: string) => void;
  onSelectFlexibleCreative: (rowId: string) => void;
  visibleOptionalColumns: OptionalColumn[];
  adAccountId?: string;
  userId?: string;
  sheetCatalogId?: string | null;
  generateRowName?: (row: AdRow) => string;
  // Selection props
  selectedRowIds: Set<string>;
  toggleRowSelection: (rowId: string) => void;
  selectAllRows: () => void;
  clearSelection: () => void;
  allRowsSelected: boolean;
  someRowsSelected: boolean;
}

// FULL OPTIONS LISTS
const AD_FORMAT_OPTIONS = [
  { id: "Single Image or Video", name: "Single Image or Video" },
  { id: "Carousel", name: "Carousel" },
  { id: "Collection", name: "Collection" },
  { id: "Catalog", name: "Catalog" },
  { id: "Flexible", name: "Flexible" },
];

const STAGE_OPTIONS = [
  { id: "Draft", name: "Draft" },
  { id: "Launched", name: "Launched" },
];

const CTA_OPTIONS = [
  { id: "NO_BUTTON", name: "No Button" },
  { id: "APPLY_NOW", name: "Apply Now" },
  { id: "BOOK_NOW", name: "Book Now" },
  { id: "CONTACT_US", name: "Contact Us" },
  { id: "DONATE_NOW", name: "Donate Now" },
  { id: "DOWNLOAD", name: "Download" },
  { id: "GET_OFFER", name: "Get Offer" },
  { id: "GET_QUOTE", name: "Get Quote" },
  { id: "GET_SHOWTIMES", name: "Get Showtimes" },
  { id: "LEARN_MORE", name: "Learn More" },
  { id: "LISTEN_NOW", name: "Listen Now" },
  { id: "ORDER_NOW", name: "Order Now" },
  { id: "PLAY_GAME", name: "Play Game" },
  { id: "REQUEST_TIME", name: "Request Time" },
  { id: "SEE_MENU", name: "See Menu" },
  { id: "SHOP_NOW", name: "Shop Now" },
  { id: "SIGN_UP", name: "Sign Up" },
  { id: "SUBSCRIBE", name: "Subscribe" },
  { id: "WATCH_MORE", name: "Watch More" },
];

export function AdSheetTable({
  rows,
  updateRow,
  updateRowBatch,
  deleteRow,
  onDuplicateRow,
  dropdownOptions,
  getAdSetsForCampaign,
  getAdSetWithDetails,
  onSelectCreative,
  onSelectFlexibleCreative,
  visibleOptionalColumns,
  adAccountId,
  userId,
  sheetCatalogId,
  generateRowName,
  selectedRowIds,
  toggleRowSelection,
  selectAllRows,
  clearSelection,
  allRowsSelected,
  someRowsSelected,
}: AdSheetTableProps) {
  const queryClient = useQueryClient();
  const [expandedTexts, setExpandedTexts] = useState<Set<string>>(new Set());
  const [expandedHeadlines, setExpandedHeadlines] = useState<Set<string>>(new Set());
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  const [carouselEditorState, setCarouselEditorState] = useState<{ rowId: string } | null>(null);
  const [collectionPopoverState, setCollectionPopoverState] = useState<{ rowId: string } | null>(null);
  const [catalogPopoverState, setCatalogPopoverState] = useState<{ rowId: string } | null>(null);
  const [previewRowId, setPreviewRowId] = useState<string | null>(null);
  const [formatSwitchDialog, setFormatSwitchDialog] = useState<{
    rowId: string;
    fromCarousel: boolean;
    targetFormat: string;
  } | null>(null);

  const previewRowIndex = previewRowId
    ? rows.findIndex(r => r.id === previewRowId)
    : null;
  const previewData = useSelectedRowPreview(rows, previewRowIndex !== -1 ? previewRowIndex : null);

  const toggleExpandTexts = (rowId: string) => {
    setExpandedTexts((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const toggleExpandHeadlines = (rowId: string) => {
    setExpandedHeadlines((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  // Helper to check if an optional column is visible
  const isVisible = (col: OptionalColumn) => visibleOptionalColumns.includes(col);

  // Helper to check if ad sets are loading for a campaign
  const isAdSetLoading = (campaignId: string | undefined): boolean => {
    if (!campaignId || !adAccountId) return false;
    // If we already have ad sets for this campaign, don't show loading
    // (background refetches shouldn't block the UI)
    const adSets = getAdSetsForCampaign(campaignId);
    if (adSets.length > 0) return false;
    const cleanId = campaignId.replace(/[^a-zA-Z0-9_]+$/g, "").trim();
    const state = queryClient.getQueryState(["ad-sets", adAccountId, cleanId]);
    return state?.fetchStatus === "fetching";
  };

  // Helper to get field rules for a row based on campaign objective + ad set metadata
  const getRowFieldRules = (row: AdRow): FieldRules => {
    if (!row.campaign?.id || !row.adSet?.id) {
      return getFieldRules(null, null);
    }
    const campaign = dropdownOptions.campaigns.find((c) => c.id === row.campaign?.id) || null;
    const adSet = getAdSetWithDetails(row.campaign.id, row.adSet.id);
    return getFieldRules(campaign, adSet, row.ctaType);
  };

  // HANDLERS - Campaign change triggers ad set fetch via useQueries in the hook
  const handleCampaignChange = (rowId: string, campaignId: string) => {
    const campaign = dropdownOptions.campaigns.find((c) => c.id === campaignId);
    const row = rows.find((r) => r.id === rowId);
    const campaignData = campaign ? { id: campaign.id, name: campaign.name } : null;

    // Update campaign and clear ad set — updateRow eagerly prefetches ad sets
    // via fetchQuery, and useQueries in the hook reactively picks up the data
    updateRow(rowId, "campaign", campaignData);
    updateRow(rowId, "adSet", null);

    // Clear stale leadgen URL placeholder when switching campaigns
    if (row?.url === "http://fb.me/") {
      updateRow(rowId, "url", "");
    }

    // Auto-rename if locked — build tempRow with new values applied
    if (generateRowName && row && row.ad_name_locked !== false && campaignData) {
      const tempRow = { ...row, campaign: campaignData, adSet: null };
      const generatedName = generateRowName(tempRow);
      if (generatedName) {
        updateRow(rowId, "adName", generatedName);
      }
    }
  };

  const handleAdSetChange = (rowId: string, adSetId: string) => {
    const row = rows.find((r) => r.id === rowId);
    const campaignId = row?.campaign?.id;

    let foundAdSet: AdSet | undefined;
    if (campaignId && dropdownOptions.adSets[campaignId]) {
      foundAdSet = dropdownOptions.adSets[campaignId].find((as) => as.id === adSetId);
    }
    if (!foundAdSet) {
      const allAdSets = Object.values(dropdownOptions.adSets).flat();
      foundAdSet = allAdSets.find((as) => as.id === adSetId);
    }

    if (!adSetId || !foundAdSet) {
      updateRowBatch(rowId, { adSet: null });
      return;
    }

    const updates: Partial<AdRow> = {
      adSet: { id: foundAdSet.id, name: foundAdSet.name },
    };

    // Auto-fill Facebook Page from promoted_object.page_id
    if (foundAdSet.promotedObject?.page_id) {
      const pageId = foundAdSet.promotedObject.page_id as string;
      const page = dropdownOptions.facebookPages.find((p) => p.id === pageId);
      if (page) {
        updates.facebookPage = { id: page.id, name: page.name };
      } else {
        updates.facebookPage = { id: pageId, name: pageId };
      }
    } else {
      // If previous ad set was ON_PAGE (had locked page), clear the auto-filled page
      const prevAdSet = row?.adSet?.id
        ? getAdSetWithDetails(campaignId || "", row.adSet.id)
        : null;
      if (prevAdSet?.destinationType === "ON_PAGE") {
        updates.facebookPage = null;
      }
    }

    // Handle ON_PAGE (Page Likes) specific auto-fills and locks
    if (foundAdSet.destinationType === "ON_PAGE") {
      updates.instagramAccount = null;
      // Force Single Image or Video — only format available for Page Likes
      if (row?.adFormat !== "Single Image or Video") {
        updates.adFormat = "Single Image or Video" as AdFormat;
      }
      // Clear fields that are not applicable for Page Likes
      updates.ctaType = "";
      updates.url = "";
      updates.displayLink = "";
      updates.utmParameters = "";
    }

    const campaign = dropdownOptions.campaigns.find((c) => c.id === campaignId);
    const rules = getFieldRules(campaign || null, foundAdSet);
    if (rules.urlAutoValue && !row?.url) {
      updates.url = rules.urlAutoValue;
    } else if (!rules.urlAutoValue && row?.url === "http://fb.me/") {
      updates.url = "";
    }
    // Auto-rename if locked
    if (generateRowName && row && row.ad_name_locked !== false && foundAdSet) {
      const tempRow = { ...row, ...updates };
      const generatedName = generateRowName(tempRow);
      if (generatedName) {
        updates.adName = generatedName;
      }
    }

    updateRowBatch(rowId, updates);
  };

  const handleFacebookPageChange = (rowId: string, pageId: string) => {
    const page = dropdownOptions.facebookPages.find((p) => p.id === pageId);
    updateRow(rowId, "facebookPage", page ? { id: page.id, name: page.name } : null);
  };

  const handleInstagramAccountChange = (rowId: string, accountId: string) => {
    const account = dropdownOptions.instagramAccounts.find((a) => a.id === accountId);
    updateRow(rowId, "instagramAccount", account ? { id: account.id, username: account.username } : null);
  };

  const handlePartnerFbPageChange = (rowId: string, pageId: string) => {
    console.log("=== PARTNER FB CHANGE DEBUG ===");
    console.log("1. pageId received:", pageId, typeof pageId);
    console.log(
      "2. dropdownOptions.partnerFacebookPages:",
      JSON.stringify(dropdownOptions.partnerFacebookPages, null, 2),
    );
    console.log("3. partnerFacebookPages length:", dropdownOptions.partnerFacebookPages?.length);

    if (!pageId) {
      console.log("4. EARLY RETURN: pageId is empty");
      updateRow(rowId, "partnerFacebookPage", null);
      return;
    }

    const page = dropdownOptions.partnerFacebookPages.find((p) => {
      console.log("   Comparing:", p.id, "===", pageId, "→", p.id === pageId);
      return p.id === pageId;
    });

    console.log("5. Found page:", page);

    if (page) {
      const updateValue = {
        id: page.id,
        username: page.username || page.name || "",
      };
      console.log("6. Calling updateRow with:", updateValue);
      updateRow(rowId, "partnerFacebookPage", updateValue);
    } else {
      console.log("6. NO PAGE FOUND - updateRow NOT called");
    }
    console.log("=== END DEBUG ===");
  };

  const handlePartnerInstagramChange = (rowId: string, accountId: string) => {
    console.log("=== PARTNER IG CHANGE DEBUG ===");
    console.log("1. accountId received:", accountId, typeof accountId);
    console.log(
      "2. dropdownOptions.partnerInstagramAccounts:",
      JSON.stringify(dropdownOptions.partnerInstagramAccounts, null, 2),
    );
    console.log("3. partnerInstagramAccounts length:", dropdownOptions.partnerInstagramAccounts?.length);

    if (!accountId) {
      console.log("4. EARLY RETURN: accountId is empty");
      updateRow(rowId, "partnerInstagramAccount", null);
      return;
    }

    const account = dropdownOptions.partnerInstagramAccounts.find((a) => {
      console.log("   Comparing:", a.id, "===", accountId, "→", a.id === accountId);
      return a.id === accountId;
    });

    console.log("5. Found account:", account);

    if (account) {
      const updateValue = {
        id: account.id,
        username: account.username || account.displayName || "",
      };
      console.log("6. Calling updateRow with:", updateValue);
      updateRow(rowId, "partnerInstagramAccount", updateValue);
    } else {
      console.log("6. NO ACCOUNT FOUND - updateRow NOT called");
    }
    console.log("=== END DEBUG ===");
  };

  // Render description cell based on ad format
  const renderDescriptionCell = (row: AdRow) => {
    const format = row.adFormat;

    // Collection: N/A
    if (format === "Collection") {
      return <span className="text-muted-foreground/50 text-xs px-2">N/A</span>;
    }

    // Carousel: Per card badge
    if (format === "Carousel") {
      return (
        <Badge variant="secondary" className="text-xs">
          Per card
        </Badge>
      );
    }

    // Flexible: Show with expandable variations (up to 5)
    if (format === "Flexible") {
      const isExpanded = expandedDescriptions.has(row.id);
      return (
        <div className="flex flex-col gap-1">
          <TextCell
            value={row.descriptions?.[0] || ""}
            onChange={(val) => {
              const newDescs = [...(row.descriptions || ["", "", "", "", ""])];
              newDescs[0] = val;
              updateRow(row.id, "descriptions", newDescs);
            }}
            placeholder="Description..."
          />
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setExpandedDescriptions((prev) => {
                const next = new Set(prev);
                if (next.has(row.id)) next.delete(row.id);
                else next.add(row.id);
                return next;
              });
            }}
            style={{
              pointerEvents: "auto",
              cursor: "pointer",
              padding: "4px 8px",
              marginTop: "4px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "12px",
              color: "#888",
              borderRadius: "4px",
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f0f0f0")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            {isExpanded ? "− Hide variations" : "+ Add variations"}
          </div>
          {isExpanded && (
            <div className="space-y-1 pl-2 border-l-2 border-border">
              {[1, 2, 3, 4].map((i) => (
                <TextCell
                  key={i}
                  value={row.descriptions?.[i] || ""}
                  onChange={(val) => {
                    const newDescs = [...(row.descriptions || ["", "", "", "", ""])];
                    newDescs[i] = val;
                    updateRow(row.id, "descriptions", newDescs);
                  }}
                  placeholder={`Variation ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    // Catalog: Single description with macro dropdown
    if (format === "Catalog") {
      return (
        <div className="flex items-center gap-1">
          <div className="flex-1">
            <TextCell
              value={row.descriptions?.[0] || ""}
              onChange={(val) => {
                const newDescs = [...(row.descriptions || ["", "", "", "", ""])];
                newDescs[0] = val;
                updateRow(row.id, "descriptions", newDescs);
                // Sync to catalogAdData for launch payload
                const isMacro = /\{\{.+\}\}/.test(val);
                const updatedCatalog = {
                  ...(row.catalogAdData || {
                    catalogId: row.catalogId || null,
                    catalogName: row.catalogName || null,
                    productSetId: row.productSetId || null,
                    productSetName: row.productSetName || null,
                    headlineType: "macro" as const,
                    headlineValue: "{{product.name}}",
                    formatType: "carousel" as const,
                    primaryText: "",
                    destinationUrl: "",
                    ctaType: "SHOP_NOW",
                    description: "",
                    automatedProductTags: false,
                  }),
                  descriptionType: isMacro ? "macro" : "custom",
                  descriptionValue: val,
                };
                updateRow(row.id, "catalogAdData", updatedCatalog);
              }}
              placeholder="Description or catalog field..."
            />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="shrink-0 h-8 w-8 flex items-center justify-center rounded border border-input bg-background hover:bg-accent transition-colors"
                title="Insert macro"
              >
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-1" side="bottom" align="end">
              <div className="flex flex-col">
                {[
                  { value: "{{product.description}}", label: "Product Description" },
                  { value: "{{product.name}}", label: "Product Name" },
                  { value: "{{product.brand}}", label: "Product Brand" },
                  { value: "{{product.price}}", label: "Product Price" },
                  { value: "{{product.current_price}}", label: "Current Price" },
                  { value: "{{product.sale_price}}", label: "Sale Price" },
                ].map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    className="text-left text-xs px-2 py-1.5 rounded hover:bg-accent transition-colors"
                    onClick={() => {
                      const newDescs = [...(row.descriptions || ["", "", "", "", ""])];
                      newDescs[0] = (newDescs[0] || "") + m.value;
                      updateRow(row.id, "descriptions", newDescs);
                      // Sync to catalogAdData for launch payload
                      const newVal = newDescs[0];
                      const isMacro = /\{\{.+\}\}/.test(newVal);
                      const updatedCatalog = {
                        ...(row.catalogAdData || {
                          catalogId: row.catalogId || null,
                          catalogName: row.catalogName || null,
                          productSetId: row.productSetId || null,
                          productSetName: row.productSetName || null,
                          headlineType: "macro" as const,
                          headlineValue: "{{product.name}}",
                          formatType: "carousel" as const,
                          primaryText: "",
                          destinationUrl: "",
                          ctaType: "SHOP_NOW",
                          description: "",
                          automatedProductTags: false,
                        }),
                        descriptionType: isMacro ? "macro" : "custom",
                        descriptionValue: newVal,
                      };
                      updateRow(row.id, "catalogAdData", updatedCatalog);
                    }}
                  >
                    {m.label}: <span className="text-muted-foreground">{m.value}</span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    // Single: Single description field
    return (
      <TextCell
        value={row.descriptions?.[0] || ""}
        onChange={(val) => {
          const newDescs = [...(row.descriptions || ["", "", "", "", ""])];
          newDescs[0] = val;
          updateRow(row.id, "descriptions", newDescs);
        }}
        placeholder="Description..."
      />
    );
  };

  // Render display link cell based on ad format
  const renderDisplayLinkCell = (row: AdRow) => {
    if (row.adFormat === "Collection") {
      return <span className="text-muted-foreground/50 text-xs px-2">N/A</span>;
    }
    return (
      <TextCell
        value={row.displayLink || ""}
        onChange={(val) => updateRow(row.id, "displayLink", val)}
        placeholder="example.com"
      />
    );
  };

  // Check if format is Collection or Catalog (no variations)
  const isNoVariationsFormat = (format: AdRow["adFormat"]) =>
    format === "Carousel" || format === "Collection" || format === "Catalog";

  return (
    <div className="w-full overflow-auto border rounded-md bg-background">
      <table className="w-full text-sm border-collapse table-fixed">
        <thead className="sticky top-0 z-10 bg-muted/50 backdrop-blur-sm">
          <tr>
            {/* Checkbox column - sticky left */}
            <th
              className="h-10 px-2 font-medium text-center border-b border-r text-muted-foreground sticky left-0 z-20 bg-muted/50"
              style={{ width: "40px", minWidth: "40px" }}
            >
              <Checkbox
                checked={allRowsSelected}
                onCheckedChange={(checked) => {
                  if (checked) {
                    selectAllRows();
                  } else {
                    clearSelection();
                  }
                }}
                aria-label="Select all rows"
                className={someRowsSelected ? "data-[state=checked]:bg-primary/50" : ""}
              />
            </th>
            {/* Core columns - always visible */}
            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "100px", minWidth: "100px" }}>Stage</th>
            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "160px", minWidth: "160px" }}>Ad Format</th>
            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "180px", minWidth: "180px" }}>Ad Name</th>
            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "160px", minWidth: "160px" }}>Campaign</th>
            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "160px", minWidth: "160px" }}>Ad Set</th>
            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "180px", minWidth: "180px" }}>Creative</th>
            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "140px", minWidth: "140px" }}>FB Page</th>
            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "140px", minWidth: "140px" }}>IG Account</th>

            {/* Optional: Instant Form */}
            {isVisible("leadgen_form") && (
              <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "160px", minWidth: "160px" }}>Instant Form</th>
            )}

            {/* Optional: Thread Profile */}
            {isVisible("threads_page_id") && (
              <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "140px", minWidth: "140px" }}>Thread Profile</th>
            )}

            {/* Optional: Partner FB Page */}
            {isVisible("partner_fb_page") && (
              <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "160px", minWidth: "160px" }}>Partner FB</th>
            )}

            {/* Optional: Partner Instagram */}
            {isVisible("partner_instagram") && (
              <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "160px", minWidth: "160px" }}>Partner IG</th>
            )}

            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "200px", minWidth: "200px" }}>Primary Text</th>
            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "180px", minWidth: "180px" }}>Headline</th>

            {/* Optional: Descriptions */}
            {isVisible("descriptions") && (
              <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "180px", minWidth: "180px" }}>Description</th>
            )}

            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "180px", minWidth: "180px" }}>URL</th>

            {/* Optional: Display Link */}
            {isVisible("display_link") && (
              <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "140px", minWidth: "140px" }}>Display Link</th>
            )}

            {/* UTM Parameters - always visible */}
            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "200px", minWidth: "200px" }}>UTM Params</th>

            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "120px", minWidth: "120px" }}>CTA</th>
            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "180px", minWidth: "180px" }}>Product Set</th>

            {/* Optional: Product Tags */}
            {isVisible("product_tags") && (
              <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "160px", minWidth: "160px" }}>Product Tags</th>
            )}

            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "90px", minWidth: "90px" }}>Status</th>
            <th className="h-10 px-2 font-medium text-left border-b border-r text-muted-foreground" style={{ width: "140px", minWidth: "140px" }}>Schedule</th>
            <th className="h-10 px-2 font-medium text-left border-b text-muted-foreground" style={{ width: "50px", minWidth: "50px" }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const rules = getRowFieldRules(row);
            const isCollection = row.adFormat === "Collection";
            const isLaunched = row.stage === "Launched";
            return (
            <tr
              key={row.id}
              data-launched={isLaunched || undefined}
              className={cn(
                "group border-b last:border-0 hover:bg-muted/30 transition-colors",
                index % 2 === 1 && "bg-muted/10",
                selectedRowIds.has(row.id) && "bg-primary/10 hover:bg-primary/15",
                isLaunched && "bg-green-500/5 hover:bg-green-500/10 opacity-80",
                isLaunched && "[&>td:not(:first-child):not(:last-child)]:pointer-events-none",
              )}
              style={{ height: "120px" }}
            >
              {/* Checkbox cell - sticky left */}
              <td className="px-2 py-2 border-r align-middle text-center sticky left-0 z-10 bg-background">
                <Checkbox
                  checked={!isLaunched && selectedRowIds.has(row.id)}
                  onCheckedChange={() => !isLaunched && toggleRowSelection(row.id)}
                  disabled={isLaunched}
                  aria-label={`Select row ${row.adName || row.id}`}
                />
              </td>
              {/* 1. Stage */}
              <td className="px-1 py-2 border-r align-middle">
                <SelectCell
                  value={(row.stage as string) || "Draft"}
                  onChange={(val) => updateRow(row.id, "stage", val)}
                  options={STAGE_OPTIONS}
                  disabled={true}
                />
              </td>

              {/* 2. Ad Format */}
              <td className={cn("px-1 py-2 border-r align-middle", rules.lockAdFormat && "bg-muted/40")}>
                {rules.lockAdFormat ? (
                  <div className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground" title="Only Single Image or Video for Page Likes">
                    <Lock className="h-3 w-3 shrink-0" />
                    <span className="truncate">Single Image or Video</span>
                  </div>
                ) : (
                <SelectCell
                  value={(row.adFormat as string) || "Single Image or Video"}
                  onChange={(val) => {
                    const currentFormat = row.adFormat;
                    if (currentFormat === "Carousel" && val !== "Carousel") {
                      if (row.carouselData?.cards?.length) {
                        setFormatSwitchDialog({ rowId: row.id, fromCarousel: true, targetFormat: val });
                        return;
                      }
                    }
                    if (val === "Catalog") {
                      updateRowBatch(row.id, {
                        adFormat: val as AdFormat,
                        catalogAdData: {
                          catalogId: row.catalogId || null,
                          catalogName: row.catalogName || null,
                          productSetId: row.productSetId || null,
                          productSetName: row.productSetName || null,
                          headlineType: "macro" as const,
                          headlineValue: "{{product.name}}",
                          formatType: "carousel" as const,
                          primaryText: "",
                          destinationUrl: "",
                          ctaType: "SHOP_NOW",
                          description: "",
                          descriptionType: "macro" as const,
                          descriptionValue: "",
                          automatedProductTags: false,
                        },
                      });
                      return;
                    }
                    if (val === "Carousel" && currentFormat !== "Carousel") {
                      updateRowBatch(row.id, {
                        adFormat: val as AdFormat,
                        creatives: { feed: null, vertical: null, horizontal: null },
                      });
                      return;
                    }
                    updateRow(row.id, "adFormat", val);
                  }}
                  options={AD_FORMAT_OPTIONS}
                />
                )}
              </td>

              {/* 3. Ad Name */}
              <td className="px-1 py-2 border-r align-middle">
                <div className="relative flex items-center">
                  <input
                    type="text"
                    value={row.adName ?? ""}
                    onChange={(e) => {
                      updateRowBatch(row.id, { adName: e.target.value, ad_name_locked: false } as Partial<AdRow>);
                    }}
                    placeholder="Ad Name..."
                    className={cn(
                      "w-full h-8 px-2 pr-7 text-sm rounded border border-transparent transition-colors",
                      "placeholder:text-muted-foreground/50",
                      "hover:border-border focus:border-primary focus:outline-none",
                      row.ad_name_locked !== false
                        ? "bg-white/5"
                        : "bg-transparent"
                    )}
                  />
                  {row.ad_name_locked !== false ? (
                    <Lock className="absolute right-1.5 h-3.5 w-3.5 text-muted-foreground/40 pointer-events-none" />
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="absolute right-1.5 p-0.5 rounded hover:bg-accent transition-colors"
                          title="Regenerate name from convention"
                        >
                          <Pencil className="h-3.5 w-3.5 text-muted-foreground/40" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-3" side="top" align="end">
                        <p className="text-xs text-muted-foreground mb-2">Regenerate name from convention?</p>
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            if (generateRowName) {
                              const generatedName = generateRowName(row);
                              if (generatedName) {
                                updateRowBatch(row.id, { adName: generatedName, ad_name_locked: true } as Partial<AdRow>);
                              }
                            }
                          }}
                        >
                          Regenerate
                        </Button>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </td>

              {/* 4. Campaign */}
              <td className="px-1 py-2 border-r align-middle">
                <SelectCell
                  value={row.campaign?.id || ""}
                  onChange={(val) => handleCampaignChange(row.id, val)}
                  options={dropdownOptions.campaigns}
                  placeholder="Select Campaign..."
                />
              </td>

              {/* 5. Ad Set */}
              <td className="px-1 py-2 border-r align-middle">
                {isAdSetLoading(row.campaign?.id) ? (
                  <div className="flex items-center gap-2 px-2 py-1 text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span className="text-xs">Loading...</span>
                  </div>
                ) : (
                  <SelectCell
                    value={row.adSet?.id || ""}
                    onChange={(val) => handleAdSetChange(row.id, val)}
                    options={getAdSetsForCampaign(row.campaign?.id || "")}
                    placeholder="Select Ad Set..."
                    disabled={!row.campaign?.id}
                  />
                )}
              </td>

              {/* 6. Creative — format-specific rendering */}
              <td className="px-1 py-2 border-r align-middle">
                {row.adFormat === "Carousel" ? (
                  <button
                    type="button"
                    onClick={() => setCarouselEditorState({ rowId: row.id })}
                    className={cn(
                      "flex items-center justify-center w-full h-full rounded gap-2 px-2 py-1",
                      "border border-transparent transition-colors",
                      "hover:border-border hover:bg-accent/50",
                      "focus:outline-none focus:border-primary",
                    )}
                  >
                    {row.carouselData?.cards?.length ? (
                      <div className="flex items-center gap-2">
                        {row.carouselData.cards[0]?.thumbnail_url && (
                          <img
                            src={row.carouselData.cards[0].thumbnail_url}
                            alt="Card 1"
                            className="h-10 w-10 rounded object-cover border border-border"
                          />
                        )}
                        <span className="text-xs text-muted-foreground font-medium">
                          {row.carouselData.cards.length} cards
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <ImagePlus className="h-4 w-4" />
                        <span>Add Cards +</span>
                      </div>
                    )}
                  </button>
                ) : isCollection ? (
                  /* Collection: IE Storefront popup button */
                  <button
                    type="button"
                    onClick={() => setCollectionPopoverState({ rowId: row.id })}
                    className={cn(
                      "flex items-center justify-center w-full h-full rounded gap-2 px-2 py-1",
                      "border border-transparent transition-colors",
                      "hover:border-border hover:bg-accent/50",
                      "focus:outline-none focus:border-primary",
                    )}
                  >
                    {row.collectionData?.creativeName ? (
                      <div className="flex items-center gap-2">
                        {row.creatives?.feed?.thumbnail ? (
                          <img
                            src={row.creatives.feed.thumbnail}
                            alt="Hero"
                            className="h-10 w-10 rounded object-cover border border-border"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded bg-muted flex items-center justify-center border border-dashed border-muted-foreground/30">
                            <ImagePlus className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <span className="text-xs text-muted-foreground font-medium truncate max-w-[100px]">
                          IE Storefront
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <ImagePlus className="h-4 w-4" />
                        <span>IE Storefront</span>
                      </div>
                    )}
                  </button>
                ) : row.adFormat === "Catalog" ? (
                  /* Catalog: DPA Settings popup button */
                  <button
                    type="button"
                    onClick={() => setCatalogPopoverState({ rowId: row.id })}
                    className={cn(
                      "flex items-center justify-center w-full h-full rounded gap-2 px-2 py-1",
                      "border border-transparent transition-colors",
                      "hover:border-border hover:bg-accent/50",
                      "focus:outline-none focus:border-primary",
                    )}
                  >
                    {row.catalogAdData?.productSetName ? (
                      <div className="flex flex-col items-start gap-0.5">
                        <span className="text-xs text-foreground font-medium truncate max-w-[140px]">
                          {row.catalogAdData.productSetName}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {row.catalogAdData.formatType === "single" ? "Single" : "Carousel"}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <ImagePlus className="h-4 w-4" />
                        <span>Configure Catalog</span>
                      </div>
                    )}
                  </button>
                ) : row.adFormat === "Flexible" ? (
                  /* Flexible: Multi-asset selector */
                  <button
                    type="button"
                    onClick={() => onSelectFlexibleCreative(row.id)}
                    className={cn(
                      "flex items-center justify-center w-full h-full rounded gap-2 px-2 py-1",
                      "border border-transparent transition-colors",
                      "hover:border-border hover:bg-accent/50",
                      "focus:outline-none focus:border-primary",
                    )}
                  >
                    {row.flexibleData?.assets?.length ? (
                      <div className="flex items-center gap-2">
                        {row.flexibleData.assets[0]?.thumbnail_url && (
                          <img
                            src={row.flexibleData.assets[0].thumbnail_url}
                            alt="Asset 1"
                            className="h-10 w-10 rounded object-cover border border-border"
                          />
                        )}
                        <span className="text-xs text-muted-foreground font-medium">
                          {row.flexibleData.assets.length} assets
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <ImagePlus className="h-4 w-4" />
                        <span>Select assets</span>
                      </div>
                    )}
                  </button>
                ) : (
                <CreativeCell
                  value={
                    row.creatives?.feed
                      ? {
                          id: row.creatives.feed.id,
                          thumbnailUrl: row.creatives.feed.thumbnail,
                          type: "image",
                          filename: row.creatives.feed.filename,
                        }
                      : null
                  }
                  verticalValue={
                    rules.singleAssetOnly ? undefined :
                    row.creatives?.vertical
                      ? {
                          id: row.creatives.vertical.id,
                          thumbnailUrl: row.creatives.vertical.thumbnail,
                          type: "image",
                          filename: row.creatives.vertical.filename,
                        }
                      : null
                  }
                  onChange={() => onSelectCreative(row.id)}
                  singleAssetOnly={rules.singleAssetOnly}
                />
                )}
              </td>

              {/* 7. Facebook Page */}
              <td className={cn("px-1 py-2 border-r align-middle", rules.lockFacebookPage && "bg-muted/40")}>
                {rules.lockFacebookPage ? (
                  <div className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground" title={rules.isPageLikes ? "Set by ad set — Page Likes" : "Set by ad set"}>
                    <Lock className="h-3 w-3 shrink-0" />
                    <span className="truncate">{row.facebookPage?.name || row.facebookPage?.id || "Locked"}</span>
                  </div>
                ) : (
                  <SelectCell
                    value={row.facebookPage?.id || ""}
                    onChange={(val) => handleFacebookPageChange(row.id, val)}
                    options={dropdownOptions.facebookPages}
                    placeholder="Select Page..."
                  />
                )}
              </td>

              {/* 8. Instagram Account */}
              <td className={cn("px-1 py-2 border-r align-middle", rules.lockInstagramAccount && "bg-muted/40")}>
                {rules.lockInstagramAccount ? (
                  <div className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground" title="Not available for Page Likes">
                    <Lock className="h-3 w-3 shrink-0" />
                    <span className="truncate text-muted-foreground/70">Not available</span>
                  </div>
                ) : (
                <SelectCell
                  value={row.instagramAccount?.id || ""}
                  onChange={(val) => handleInstagramAccountChange(row.id, val)}
                  options={dropdownOptions.instagramAccounts.map((a) => ({ id: a.id, name: a.username }))}
                  placeholder="Select IG..."
                />
                )}
              </td>

              {/* Optional: Instant Form */}
              {isVisible("leadgen_form") && (
                <td className="px-1 py-2 border-r align-middle">
                  {rules.hideLeadgenForm ? (
                    <span className="text-muted-foreground/50 text-xs px-2 italic">Hidden</span>
                  ) : (
                    <div className={rules.leadgenFormRequired ? "ring-1 ring-primary/30 rounded" : ""}>
                      <InstantFormCell
                        value={row.leadgenFormId}
                        onChange={(formId) => updateRow(row.id, "leadgenFormId", formId)}
                        userId={userId}
                        adSet={row.adSet ? getAdSetWithDetails(row.campaign?.id || "", row.adSet.id) : null}
                        facebookPage={row.facebookPage}
                      />
                    </div>
                  )}
                </td>
              )}

              {/* Optional: Thread Profile */}
              {isVisible("threads_page_id") && (
                <td className="px-1 py-2 border-r align-middle">
                  <TextCell
                    value={row.threadsPageId || ""}
                    onChange={(val) => updateRow(row.id, "threadsPageId", val)}
                    placeholder="Thread ID..."
                  />
                </td>
              )}

              {/* Optional: Partner FB Page */}
              {isVisible("partner_fb_page") && (
                <td className="px-1 py-2 border-r align-middle">
                  <div className="relative">
                    {row.partnerFacebookPage?.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-muted hover:bg-destructive/20 z-10"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateRow(row.id, "partnerFacebookPage", null);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                    <SelectCell
                      value={row.partnerFacebookPage?.id || ""}
                      onChange={(val) => handlePartnerFbPageChange(row.id, val)}
                      options={dropdownOptions.partnerFacebookPages.map((p) => ({ id: p.id, name: p.username }))}
                      placeholder="Select Partner FB..."
                    />
                  </div>
                </td>
              )}

              {/* Optional: Partner Instagram */}
              {isVisible("partner_instagram") && (
                <td className="px-1 py-2 border-r align-middle">
                  <div className="relative">
                    {row.partnerInstagramAccount?.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-muted hover:bg-destructive/20 z-10"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateRow(row.id, "partnerInstagramAccount", null);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                    <SelectCell
                      value={row.partnerInstagramAccount?.id || ""}
                      onChange={(val) => handlePartnerInstagramChange(row.id, val)}
                      options={dropdownOptions.partnerInstagramAccounts.map((a) => ({ id: a.id, name: a.username }))}
                      placeholder="Select Partner IG..."
                    />
                  </div>
                </td>
              )}

              {/* 9. Primary Text — Collection/Carousel: only 1, no variations */}
              <td className="px-1 py-2 border-r align-top">
                <div className="flex flex-col gap-1">
                  <TextCell
                    value={row.primaryTexts?.[0] || ""}
                    onChange={(val) => {
                      const updated = [...(row.primaryTexts || ["", "", "", "", ""])];
                      updated[0] = val;
                      updateRow(row.id, "primaryTexts", updated);
                    }}
                    placeholder="Primary text..."
                  />
                  {/* Hide variations toggle for Carousel, Collection, Catalog */}
                  {!isNoVariationsFormat(row.adFormat) && (
                    <>
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleExpandTexts(row.id);
                        }}
                        style={{
                          pointerEvents: "auto",
                          cursor: "pointer",
                          padding: "4px 8px",
                          marginTop: "4px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          fontSize: "12px",
                          color: "#888",
                          borderRadius: "4px",
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f0f0f0")}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        {expandedTexts.has(row.id) ? "− Hide variations" : "+ Add variations"}
                      </div>
                      {expandedTexts.has(row.id) && (
                        <div className="space-y-1 pl-2 border-l-2 border-border">
                          {[1, 2, 3, 4].map((i) => (
                            <TextCell
                              key={i}
                              value={row.primaryTexts?.[i] || ""}
                              onChange={(val) => {
                                const updated = [...(row.primaryTexts || ["", "", "", "", ""])];
                                updated[i] = val;
                                updateRow(row.id, "primaryTexts", updated);
                              }}
                              placeholder={`Variation ${i + 1}`}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </td>

              {/* 10. Headline — Carousel: "Per card", Collection/Catalog: 1 only (no variations), others: expandable */}
              <td className={cn("px-1 py-2 border-r align-top", rules.hideHeadline && "bg-muted/40")}>
                {rules.hideHeadline ? (
                  <div className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground rounded h-8" title="Not available for Page Likes">
                    <Lock className="h-3 w-3 shrink-0" />
                    <span className="truncate text-muted-foreground/70">Auto from page</span>
                  </div>
                ) : row.adFormat === "Carousel" ? (
                  <div className="flex items-center h-full px-2">
                    <Badge variant="secondary" className="text-xs bg-muted text-muted-foreground">
                      Per card
                    </Badge>
                  </div>
                ) : isCollection ? (
                  /* Collection: single headline, no variations */
                  <TextCell
                    value={row.headlines?.[0] || ""}
                    onChange={(val) => {
                      const updated = [...(row.headlines || ["", "", "", "", ""])];
                      updated[0] = val;
                      updateRow(row.id, "headlines", updated);
                    }}
                    placeholder="Headline..."
                  />
                ) : row.adFormat === "Catalog" ? (
                  /* Catalog: single headline with macro dropdown */
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <div className="flex-1">
                        <TextCell
                          value={row.headlines?.[0] || ""}
                          onChange={(val) => {
                            const updated = [...(row.headlines || ["", "", "", "", ""])];
                            updated[0] = val;
                            updateRow(row.id, "headlines", updated);
                            // Sync to catalogAdData for launch payload
                            const isMacro = /\{\{.+\}\}/.test(val);
                            const updatedCatalog = {
                              ...(row.catalogAdData || {
                                catalogId: row.catalogId || null,
                                catalogName: row.catalogName || null,
                                productSetId: row.productSetId || null,
                                productSetName: row.productSetName || null,
                                formatType: "carousel" as const,
                                primaryText: "",
                                destinationUrl: "",
                                ctaType: "SHOP_NOW",
                                description: "",
                                descriptionType: "macro" as const,
                                descriptionValue: "",
                                automatedProductTags: false,
                              }),
                              headlineType: isMacro ? "macro" : "custom",
                              headlineValue: val,
                            };
                            updateRow(row.id, "catalogAdData", updatedCatalog);
                          }}
                          placeholder="Headline or catalog field..."
                        />
                      </div>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="shrink-0 h-8 w-8 flex items-center justify-center rounded border border-input bg-background hover:bg-accent transition-colors"
                            title="Insert macro"
                          >
                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48 p-1" side="bottom" align="end">
                          <div className="flex flex-col">
                            {CATALOG_HEADLINE_MACROS.map((m) => (
                              <button
                                key={m.value}
                                type="button"
                                className="text-left text-xs px-2 py-1.5 rounded hover:bg-accent transition-colors"
                                onClick={() => {
                                  const updated = [...(row.headlines || ["", "", "", "", ""])];
                                  updated[0] = (updated[0] || "") + m.value;
                                  updateRow(row.id, "headlines", updated);
                                  // Sync to catalogAdData for launch payload
                                  const newVal = updated[0];
                                  const isMacro = /\{\{.+\}\}/.test(newVal);
                                   const updatedCatalog = {
                                    ...(row.catalogAdData || {
                                      catalogId: row.catalogId || null,
                                      catalogName: row.catalogName || null,
                                      productSetId: row.productSetId || null,
                                      productSetName: row.productSetName || null,
                                      formatType: "carousel" as const,
                                      primaryText: "",
                                      destinationUrl: "",
                                      ctaType: "SHOP_NOW",
                                      description: "",
                                      descriptionType: "macro" as const,
                                      descriptionValue: "",
                                      automatedProductTags: false,
                                    }),
                                    headlineType: isMacro ? "macro" : "custom",
                                    headlineValue: newVal,
                                  };
                                  updateRow(row.id, "catalogAdData", updatedCatalog);
                                }}
                              >
                                {m.label}: <span className="text-muted-foreground">{m.value}</span>
                              </button>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                ) : (
                <div className="flex flex-col gap-1">
                  <TextCell
                    value={row.headlines?.[0] || ""}
                    onChange={(val) => {
                      const updated = [...(row.headlines || ["", "", "", "", ""])];
                      updated[0] = val;
                      updateRow(row.id, "headlines", updated);
                    }}
                    placeholder="Headline..."
                  />
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleExpandHeadlines(row.id);
                    }}
                    style={{
                      pointerEvents: "auto",
                      cursor: "pointer",
                      padding: "4px 8px",
                      marginTop: "4px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "12px",
                      color: "#888",
                      borderRadius: "4px",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f0f0f0")}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    {expandedHeadlines.has(row.id) ? "− Hide variations" : "+ Add variations"}
                  </div>
                  {expandedHeadlines.has(row.id) && (
                    <div className="space-y-1 pl-2 border-l-2 border-border">
                      {[1, 2, 3, 4].map((i) => (
                        <TextCell
                          key={i}
                          value={row.headlines?.[i] || ""}
                          onChange={(val) => {
                            const updated = [...(row.headlines || ["", "", "", "", ""])];
                            updated[i] = val;
                            updateRow(row.id, "headlines", updated);
                          }}
                          placeholder={`Variation ${i + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
                )}
              </td>

              {/* Optional: Description */}
              {isVisible("descriptions") && (
                <td className={cn("px-1 py-2 border-r", rules.hideDescription ? "bg-muted/40 align-middle" : row.adFormat === "Flexible" ? "align-top" : "align-middle")}>
                  {rules.hideDescription ? (
                    <div className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground rounded h-8" title="Not available for Page Likes">
                      <Lock className="h-3 w-3 shrink-0" />
                      <span className="truncate text-muted-foreground/70">N/A</span>
                    </div>
                  ) : renderDescriptionCell(row)}
                </td>
              )}

              {/* 11. URL */}
              <td className={cn("px-1 py-2 border-r align-middle", rules.hideLinkUrl && "bg-muted/40")}>
                {rules.hideLinkUrl ? (
                  <div className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground rounded h-8" title="Not available for Page Likes">
                    <Lock className="h-3 w-3 shrink-0" />
                    <span className="truncate text-muted-foreground/70">Auto from page</span>
                  </div>
                ) : rules.hideUrl && !rules.showUrlForCta ? (
                  <span className="text-muted-foreground/50 text-xs px-2 italic">Hidden</span>
                ) : row.url === "http://fb.me/" ? (
                  <span className="text-muted-foreground/50 text-xs px-2"></span>
                ) : (
                  <div className={rules.urlRequired ? "ring-1 ring-primary/30 rounded" : ""}>
                    <TextCell
                      value={row.url || ""}
                      onChange={(val) => updateRow(row.id, "url", val)}
                      placeholder={rules.urlRequired ? "Required URL..." : "https://..."}
                    />
                  </div>
                )}
              </td>

              {/* Optional: Display Link */}
              {isVisible("display_link") && (
                <td className={cn("px-1 py-2 border-r align-middle", rules.hideDisplayLink && "bg-muted/40")}>
                  {rules.hideDisplayLink ? (
                    <div className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground rounded h-8" title="Not available for Page Likes">
                      <Lock className="h-3 w-3 shrink-0" />
                      <span className="truncate text-muted-foreground/70">N/A</span>
                    </div>
                  ) : renderDisplayLinkCell(row)}
                </td>
              )}

              {/* UTM Parameters - always visible */}
              <td className={cn("px-1 py-2 border-r align-middle", rules.hideUtmParameters && "bg-muted/40")}>
                {rules.hideUtmParameters ? (
                  <div className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground rounded h-8" title="Not available for Page Likes">
                    <Lock className="h-3 w-3 shrink-0" />
                    <span className="truncate text-muted-foreground/70">N/A</span>
                  </div>
                ) : (
                  <TextCell
                    value={row.utmParameters || ""}
                    onChange={(val) => updateRow(row.id, "utmParameters", val)}
                    placeholder="utm_source=facebook&..."
                  />
                )}
              </td>

              {/* 12. CTA */}
              <td className={cn("px-1 py-2 border-r align-middle", rules.hideCta && "bg-muted/40")}>
                {rules.hideCta ? (
                  <div className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground rounded h-8" title="Not available for Page Likes — Meta forces LIKE_PAGE">
                    <Lock className="h-3 w-3 shrink-0" />
                    <span className="truncate text-muted-foreground/70">LIKE_PAGE (auto)</span>
                  </div>
                ) : (
                  <SelectCell
                    value={row.ctaType || ""}
                    onChange={(val) => updateRow(row.id, "ctaType", val)}
                    options={CTA_OPTIONS}
                    placeholder="Select a CTA button"
                  />
                )}
              </td>

              {/* 13. Product Set */}
              <td className="px-1 py-2 border-r align-middle">
                {rules.hideProductSet ? (
                  <span className="text-muted-foreground/50 text-xs px-2 italic">Hidden</span>
                ) : (
                  <ProductSetCell
                    value={{
                      catalogId: row.catalogId,
                      catalogName: row.catalogName,
                      productSetId: row.productSetId,
                      productSetName: row.productSetName,
                    }}
                    onChange={(val) => {
                      const updates: Record<string, unknown> = {
                        catalogId: val.catalogId,
                        catalogName: val.catalogName,
                        productSetId: val.productSetId,
                        productSetName: val.productSetName,
                      };
                      // Keep catalogAdData in sync for Catalog format
                      if (row.adFormat === "Catalog") {
                        updates.catalogAdData = {
                          ...(row.catalogAdData || {
                            headlineType: "macro" as const,
                            headlineValue: "{{product.name}}",
                            formatType: "carousel" as const,
                            primaryText: "",
                            destinationUrl: "",
                            ctaType: "SHOP_NOW",
                            description: "",
                            descriptionType: "macro" as const,
                            descriptionValue: "",
                            automatedProductTags: false,
                          }),
                          catalogId: val.catalogId,
                          catalogName: val.catalogName,
                          productSetId: val.productSetId,
                          productSetName: val.productSetName,
                        };
                      }
                      updateRowBatch(row.id, updates);
                    }}
                    adFormat={row.adFormat}
                    userId={userId}
                    adAccountId={adAccountId}
                    sheetCatalogId={sheetCatalogId}
                  />
                )}
              </td>

              {/* Optional: Product Tags — hidden for Collection, Catalog, Flexible */}
              {isVisible("product_tags") && (
                <td className="px-1 py-2 border-r align-middle">
                  {rules.hideProductTags ? (
                    <span className="text-muted-foreground/50 text-xs px-2 italic">Hidden</span>
                  ) : (row.adFormat === "Single Image or Video" || row.adFormat === "Carousel") ? (
                    <ProductTagsPopover
                      value={row.productTags || []}
                      onChange={(tags) => updateRow(row.id, "productTags", tags)}
                      catalogId={row.catalogId || sheetCatalogId}
                      userId={userId}
                    />
                  ) : (
                    <span className="text-muted-foreground/50 text-xs px-2">N/A</span>
                  )}
                </td>
              )}

              {/* 14. Status */}
              <td className="px-1 py-2 border-r align-middle">
                <StatusCell
                  value={row.status === "ACTIVE" ? "active" : "paused"}
                  onChange={(val) => updateRow(row.id, "status", val === "active" ? "ACTIVE" : "PAUSED")}
                />
              </td>

              {/* 15. Schedule */}
              <td className="px-1 py-2 border-r align-middle">
                <ScheduleCell
                  value={{
                    start: row.ad_schedule_start || null,
                    end: row.ad_schedule_end || null,
                  }}
                  onChange={(val) => {
                    console.log("AdSheetTable ScheduleCell onChange received:", val);
                    console.log("Updating row:", row.id);
                    console.log("Setting ad_schedule_start to:", val.start);
                    console.log("Setting ad_schedule_end to:", val.end);
                    updateRow(row.id, "ad_schedule_start", val.start);
                    updateRow(row.id, "ad_schedule_end", val.end);
                  }}
                />
              </td>

              {/* 16. Actions */}
              <td className="px-1 py-2 text-center align-middle">
                <div className="flex items-center justify-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-6 h-6 text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/50"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewRowId(row.id);
                    }}
                    title="Preview ad"
                  >
                    <Eye className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-6 h-6 text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/50"
                    onClick={() => onDuplicateRow(row.id)}
                    title="Duplicate row"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                  {!isLaunched && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-6 h-6 text-destructive/50 hover:text-destructive hover:bg-destructive/10"
                    onClick={() => deleteRow(row.id)}
                    title="Delete row"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                  )}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>

      {/* Format switch confirmation dialog */}
      <AlertDialog open={!!formatSwitchDialog} onOpenChange={(open) => !open && setFormatSwitchDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch from Carousel?</AlertDialogTitle>
            <AlertDialogDescription>
              Switching from Carousel will clear your card configuration. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (formatSwitchDialog) {
                  updateRowBatch(formatSwitchDialog.rowId, {
                    adFormat: formatSwitchDialog.targetFormat as AdFormat,
                    carouselData: null,
                  });
                  setFormatSwitchDialog(null);
                }
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Carousel Card Editor Modal */}
      {carouselEditorState && (() => {
        const row = rows.find(r => r.id === carouselEditorState.rowId);
        if (!row) return null;
        return (
          <CarouselCardEditor
            isOpen={true}
            onClose={() => setCarouselEditorState(null)}
            adAccountId={adAccountId || ""}
            userId={userId || ""}
            existingCards={row.carouselData?.cards || []}
            onSave={(cards) => {
              updateRow(carouselEditorState.rowId, "carouselData", { cards });
              setCarouselEditorState(null);
            }}
            defaultCta={row.ctaType}
            defaultUrl={row.url}
          />
        );
      })()}

      {/* Collection IE Storefront Popover */}
      {collectionPopoverState && (() => {
        const row = rows.find(r => r.id === collectionPopoverState.rowId);
        if (!row) return null;
        return (
          <CollectionPopover
            isOpen={true}
            onClose={() => setCollectionPopoverState(null)}
            value={row.collectionData}
            onChange={(data) => {
              updateRow(collectionPopoverState.rowId, "collectionData", data);
              // Auto-regenerate ad name after collection data changes
              const currentRow = rows.find(r => r.id === collectionPopoverState.rowId);
              if (currentRow && currentRow.ad_name_locked !== false && generateRowName) {
                const tempRow = { ...currentRow, collectionData: data };
                const generatedName = generateRowName(tempRow);
                if (generatedName) {
                  updateRow(collectionPopoverState.rowId, "adName", generatedName);
                }
              }
            }}
            onSelectCreative={() => {
              // Open the creative library selector for the cover asset
              onSelectCreative(collectionPopoverState.rowId);
            }}
          />
        );
      })()}

      {/* Catalog DPA Settings Popover */}
      {catalogPopoverState && (() => {
        const row = rows.find(r => r.id === catalogPopoverState.rowId);
        if (!row) return null;
        return (
          <CatalogPopover
            isOpen={true}
            onClose={() => setCatalogPopoverState(null)}
            value={row.catalogAdData}
            onChange={(data) => {
              updateRowBatch(catalogPopoverState.rowId, {
                catalogAdData: data,
                catalogId: data.catalogId,
                catalogName: data.catalogName,
                productSetId: data.productSetId,
                productSetName: data.productSetName,
              });
              setCatalogPopoverState(null);
            }}
            userId={userId}
            adAccountId={adAccountId}
          />
        );
      })()}
      {/* Ad Preview Modal */}
      <Dialog open={previewRowId !== null} onOpenChange={(open) => { if (!open) setPreviewRowId(null); }}>
        <DialogContent className="max-w-[420px] p-0 gap-0 overflow-hidden">
          <AdPreviewPanel
            previewData={previewData}
            userId={userId}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
